import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import BookmarkSchema from "marketplace/bookmarks/_schema/bookmark-schema";
import { z } from "zod";

export type BookmarkedIndividualJSON = z.infer<typeof BookmarkedIndividualSchema.BookmarkedIndividualJSONZ>;
export type BookmarkedIndividualParams = z.infer<typeof BookmarkedIndividualSchema.BookmarkedIndividualZ>;

export default class BookmarkedIndividualSchema {
  public static readonly BookmarkedIndividualJSONZ = z.object({
    ...BookmarkSchema.BookmarkJSONZ.shape,
    individualId: z.uuid(),
  });
  public static readonly BookmarkedIndividualZ = z.object({
    ...BookmarkSchema.BookmarkZ.shape,
    individualId: z.instanceof(Guid),
  });

  public static validate(values: unknown): BookmarkedIndividualParams {
    return parseOrThrow(BookmarkedIndividualSchema.BookmarkedIndividualZ, values, {
      logError: true,
      schemaName: "BookmarkedIndividualSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedIndividualJSON {
    return parseOrThrow(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedIndividualSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static validateJSONArray(values: unknown): BookmarkedIndividualJSON[] {
    return parseOrThrow(z.array(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ), values, {
      logError: true,
      schemaName: "BookmarkedIndividualSchema",
      operation: "validateJSONArray",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is BookmarkedIndividualParams {
    try {
      BookmarkedIndividualSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is BookmarkedIndividualJSON {
    try {
      BookmarkedIndividualSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: BookmarkedIndividualJSON): BookmarkedIndividualParams {
    const parsed = parseOrThrow(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ, data, {
      logError: true,
      schemaName: "BookmarkedIndividualSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return BookmarkedIndividualSchema.codec.decode(parsed);
  }

  public static deserializeArray(data: BookmarkedIndividualJSON[]): BookmarkedIndividualParams[] {
    return data.map((item) => BookmarkedIndividualSchema.deserialize(item));
  }

  public static serialize(document: BookmarkedIndividualParams): BookmarkedIndividualJSON {
    const parsed = BookmarkedIndividualSchema.validate(document);
    return BookmarkedIndividualSchema.codec.encode(parsed);
  }

  public static serializeArray(documents: BookmarkedIndividualParams[]): BookmarkedIndividualJSON[] {
    return documents.map((item) => BookmarkedIndividualSchema.serialize(item));
  }

  private static readonly codec = z.codec(
    BookmarkedIndividualSchema.BookmarkedIndividualJSONZ,
    BookmarkedIndividualSchema.BookmarkedIndividualZ,
    {
      decode: (data) => ({
        ...BookmarkSchema.deserialize(data),
        individualId: Guid.fromJSON(data.individualId)!,
      }),
      encode: (bookmark) => ({
        ...BookmarkSchema.serialize(bookmark),
        individualId: bookmark.individualId.toJSON(),
      }),
    }
  );
}
